import { Context, Effect, Layer } from 'effect';
import * as KeyValueStore from 'effect/persistence/KeyValueStore';

/**
 * The keys the host app shares with the Configurator through session storage.
 *
 * Kept in one place so the Configurator never spells a key out anywhere else. Adding a key here and a matching member below is the whole cost of exposing a new one.
 */
export const sessionStorageKeys = {
	account: 'User.Account',
	authToken: 'User.Authorization',
} as const;

/** A raw stored value, `undefined` when the host app has not written the key. */
type StoredValue = Effect.Effect<string | undefined, KeyValueStore.KeyValueStoreError>;

/**
 * The raw values the Configurator reads out of the host app's session storage.
 *
 * Deliberately raw: whether an absent value is an error, and how a present one is parsed, differs per key and belongs to the service that owns it.
 */
export interface SessionStorage {
	/** The signed-in account as JSON, under `User.Account`. */
	readonly account: StoredValue;
	/** The Cognito access token, under `User.Authorization`. */
	readonly authToken: StoredValue;
}

/** Provides the host app's session storage to the Configurator. */
export class SessionStorageService extends Context.Service<SessionStorageService, SessionStorage>()('@acies/platform/SessionStorageService', {
	/** Binds each key to whichever `KeyValueStore` is in context. */
	make: Effect.gen(function* () {
		const store = yield* KeyValueStore.KeyValueStore;
		return {
			account: store.get(sessionStorageKeys.account),
			authToken: store.get(sessionStorageKeys.authToken),
		};
	}),
}) {
	/** The key bindings over any `KeyValueStore`, leaving the store to the caller. */
	static readonly layerNoDeps: Layer.Layer<SessionStorageService, never, KeyValueStore.KeyValueStore> = Layer.effect(this)(this.make);

	/**
	 * Backed by the browser's own session storage.
	 *
	 * One layer for every consuming service rather than one each: Effect memoises layers by reference, so sharing this constant means a single store is built per runtime.
	 */
	static readonly layer: Layer.Layer<SessionStorageService> = SessionStorageService.layerNoDeps.pipe(Layer.provide(KeyValueStore.layerStorage(() => window.sessionStorage)));

	/** Pins the stored values explicitly. Keys left out read as absent. */
	static readonly layerValues = (values: Partial<Record<keyof SessionStorage, string>>): Layer.Layer<SessionStorageService> =>
		Layer.succeed(
			SessionStorageService,
			SessionStorageService.of({
				account: Effect.succeed(values.account),
				authToken: Effect.succeed(values.authToken),
			}),
		);
}
